import React from "react";
import { useNavigate } from "react-router-dom";
import { Users, Calendar, CheckCircle, ArrowRight, PlayCircle } from "lucide-react";
import { Button } from "../ui/button";

export const HeroSection = () => {
  const navigate = useNavigate();
  const stats = [
    { value: "25,200+", label: "Old Students", icon: Users },
    { value: "56+", label: "Years", icon: Calendar },
  ];

  return (
    <section className="relative bg-[#002147] text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Motto */}
            <p className="text-[#FF6B00] italic text-lg mb-4 font-medium hero-animate-motto">
              "Train up a child in the way he should go, and when he is old, he will not depart from it."
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 hero-animate-title">
              Nurturing Tomorrow's <span className="text-[#FF6B00]">Leaders</span> Today
            </h1>
            <div className="flex flex-wrap gap-4 mb-12 hero-animate-buttons">
              <Button 
                className="bg-[#FF6B00] hover:bg-[#E65C00] text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
                onClick={() => navigate("/contact")}
                data-testid="apply-now-btn"
              >
                Contact Us <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-lg transition-all"
                onClick={() => navigate("/virtual-tour")}
                data-testid="virtual-tour-btn"
              >
                <PlayCircle className="mr-2" size={20} /> Virtual Tour
              </Button>
            </div>

            {/* Stats */}
            <div className="flex gap-8 hero-animate-stats">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <stat.icon className="w-8 h-8 mx-auto mb-2 text-[#FF6B00]" />
                  <div className="text-3xl font-bold">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Mobile Image Banner */}
            <div className="lg:hidden mt-8 grid grid-cols-2 gap-3" data-testid="mobile-hero-images">
              <img 
                src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/4j6m41gm_WhatsApp%20Image%202026-01-20%20at%206.40.14%20AM%20%282%29.jpeg" 
                alt="School students" 
                className="rounded-xl shadow-lg w-full h-32 object-cover hero-animate-image-1"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/wu9rtfep_WhatsApp%20Image%202026-01-20%20at%206.40.15%20AM%20%282%29.jpeg" 
                alt="Student activities" 
                className="rounded-xl shadow-lg w-full h-32 object-cover hero-animate-image-2"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/5h09vz7o_WhatsApp%20Image%202026-01-20%20at%206.40.15%20AM%20%283%29%20-%20Copy.jpeg" 
                alt="Learning activities" 
                className="rounded-xl shadow-lg w-full h-32 object-cover hero-animate-image-3"
              />
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 shadow-lg flex flex-col justify-center hero-animate-image-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="text-green-400" size={18} />
                  <span className="font-semibold text-sm">100% College</span>
                </div>
                <p className="text-xs text-gray-300">Class of 2025</p>
              </div>
            </div>
          </div>

          {/* Right Content - Image Grid (Desktop) */}
          <div className="hidden lg:grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/4j6m41gm_WhatsApp%20Image%202026-01-20%20at%206.40.14%20AM%20%282%29.jpeg" 
                alt="Graduation ceremony" 
                className="rounded-xl shadow-lg w-full h-48 object-cover hero-animate-image-1"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/wu9rtfep_WhatsApp%20Image%202026-01-20%20at%206.40.15%20AM%20%282%29.jpeg" 
                alt="Student playing keyboard" 
                className="rounded-xl shadow-lg w-full h-40 object-cover hero-animate-image-2"
              />
            </div>
            <div className="space-y-4 pt-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/5h09vz7o_WhatsApp%20Image%202026-01-20%20at%206.40.15%20AM%20%283%29%20-%20Copy.jpeg" 
                alt="Students cooking" 
                className="rounded-xl shadow-lg w-full h-40 object-cover hero-animate-image-3"
              />
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 shadow-lg hero-animate-image-4">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle className="text-green-400" size={24} />
                  <span className="font-semibold">100% College Acceptance</span>
                </div>
                <p className="text-sm text-gray-300">Class of 2025</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave */}
      <div className="hero-wave">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.66,118.92,114.17,97.14,175.55,82.26,237.63,67.21,259.86,67.23,321.39,56.44Z" fill="#FACC15"></path>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
